import { apiContainer } from '@vlabs/api-bindings';
import type { LunaClient } from '@vlabs/api-bindings';

import type {
  TClassifyDeepfakeRequest,
  TClassifyDeepfakeResponse,
  TClassifyDeepfakeTaskResponse,
  TClassifyDeepfakeTaskResult,
} from './types';

const getLunaClient = (): LunaClient => {
  const client = apiContainer.lunaClient;

  if (!client) {
    throw new Error('Luna client is not initialized');
  }

  return client as LunaClient;
};

export const classifyDeepfake = async (
  servicePath: string,
  payload: TClassifyDeepfakeRequest,
): Promise<TClassifyDeepfakeResponse> => {
  const response = await getLunaClient().deepfakeClassifier.classify(servicePath, payload);
  return response.data as TClassifyDeepfakeResponse;
};

export const getClassifyDeepfakeTask = async (
  servicePath: string,
  taskId: string,
): Promise<TClassifyDeepfakeTaskResponse> => {
  const response = await getLunaClient().deepfakeClassifier.getTask(servicePath, taskId);
  return response.data as TClassifyDeepfakeTaskResponse;
};

export const getClassifyDeepfakeResultByUrl = async (
  resultUrl: string,
): Promise<TClassifyDeepfakeTaskResult> => {
  const response = await getLunaClient().deepfakeClassifier.getResultByUrl(resultUrl);
  return response.data as TClassifyDeepfakeTaskResult;
};

const sleep = async (ms: number): Promise<void> => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

interface TPollClassifyDeepfakeTaskParams {
  taskId: string;
  intervalMs?: number;
  maxAttempts?: number;
  servicePath: string;
}

export const pollClassifyDeepfakeTask = async ({
  taskId,
  intervalMs = 2000,
  maxAttempts = 30,
  servicePath,
}: TPollClassifyDeepfakeTaskParams): Promise<TClassifyDeepfakeTaskResult> => {
  let attempt = 0;

  /* eslint-disable no-await-in-loop */
  while (attempt < maxAttempts) {
    const task = await getClassifyDeepfakeTask(servicePath, taskId);

    if (task.status === 'done') {
      if ('result' in task && task.result) {
        return task.result;
      }

      if ('result_url' in task && task.result_url) {
        return getClassifyDeepfakeResultByUrl(task.result_url);
      }
    }

    if (task.status === 'failed') {
      throw new Error(task.error?.message || 'Task failed');
    }

    attempt += 1;
    await sleep(intervalMs);
  }
  /* eslint-enable no-await-in-loop */

  throw new Error('Deepfake classification polling timeout');
};
